import type { CompositionKind } from 'types/composition';
import type { ComposerGalleryEntry, ComposerGalleryKindFilter } from './ComposerGalleryOrganism';

export type ComposerGallerySectionKey = 'space' | CompositionKind | 'builtin';

export interface ComposerGallerySection {
  key: ComposerGallerySectionKey;
  entries: ComposerGalleryEntry[];
}

/**
 * Fixed display order (EAINT-844 Phase F7a): Spaces · Surfaces · Sections · Fragments · Built-ins.
 *
 * Declared as an exhaustive `Record` over `ComposerGallerySectionKey` rather than an array so that
 * widening `CompositionKind` is a compile error here instead of silently dropping the new kind's
 * cards out of the gallery.
 */
const GALLERY_SECTION_RANK: Record<ComposerGallerySectionKey, number> = {
  space: 0,
  surface: 1,
  section: 2,
  fragment: 3,
  builtin: 4,
};

export const GALLERY_SECTION_ORDER: ComposerGallerySectionKey[] = (
  Object.keys(GALLERY_SECTION_RANK) as ComposerGallerySectionKey[]
).sort((a, b) => GALLERY_SECTION_RANK[a] - GALLERY_SECTION_RANK[b]);

const sectionKeyOf = (entry: ComposerGalleryEntry): ComposerGallerySectionKey => {
  if (entry.builtIn) return 'builtin';
  if (entry.targetKind === 'space') return 'space';
  return entry.kind as CompositionKind;
};

/**
 * Does this entry belong under the active kind facet?
 *
 * Matching is on the entry's own KIND, never on the section it happens to be bucketed into. Every
 * entry already carries the right value: a composition's `kind` is its `CompositionKind`, a space's
 * is `'space'`, and a built-in's is its catalog `role` (`ComposeGalleryPage` sets `kind: entry.role`).
 * So a built-in surface matches the `surface` facet exactly as a composed surface does.
 *
 * This is deliberately decoupled from `sectionKeyOf` — bucketing groups by PROVENANCE (built-ins
 * keep their own section so the no-clone rule stays visible), while filtering narrows by TYPE.
 * Conflating the two was the F7a defect: built-ins were bucketed as `'builtin'` and then compared
 * against the facet by that same key, so `surface` and `section` could never match one and the
 * built-ins silently vanished from every typed facet.
 */
export const matchesGalleryFilter = (
  entry: ComposerGalleryEntry,
  filter: ComposerGalleryKindFilter
): boolean => filter === 'all' || entry.kind === filter;

/**
 * Maps a kind-facet chip to the one section it isolates, for callers that need the section identity
 * itself (headings, empty states). `'all'` isolates nothing. `'primitive'` resolves to Built-ins
 * because compositions are never `kind: 'primitive'` — primitives are exclusively code-backed — so
 * that facet's results are always built-ins.
 *
 * NOTE: this does NOT decide membership. Use `matchesGalleryFilter` for that; a typed facet can
 * legitimately produce entries in two sections (e.g. `surface` → composed Surfaces AND built-in
 * surfaces under Built-ins).
 */
export const sectionKeyForFilter = (filter: ComposerGalleryKindFilter): ComposerGallerySectionKey | null => {
  if (filter === 'all') return null;
  if (filter === 'primitive') return 'builtin';
  return filter as ComposerGallerySectionKey;
};

/**
 * Groups gallery entries into their typed sections in fixed display order, applying the active
 * kind-facet filter and omitting any section with no matching entries (EAINT-844 Phase F7a).
 */
export function groupGalleryEntriesIntoSections(
  entries: ComposerGalleryEntry[],
  kindFilter: ComposerGalleryKindFilter
): ComposerGallerySection[] {
  const buckets = new Map<ComposerGallerySectionKey, ComposerGalleryEntry[]>();
  for (const entry of entries) {
    if (!matchesGalleryFilter(entry, kindFilter)) continue;
    const key = sectionKeyOf(entry);
    const bucket = buckets.get(key);
    if (bucket) {
      bucket.push(entry);
    } else {
      buckets.set(key, [entry]);
    }
  }
  // Sort the buckets that actually exist rather than intersecting with a fixed list, so an
  // unranked key (a kind added to the union, or a non-built-in `catalog` row) sorts to the end and
  // still renders instead of vanishing while the gallery claims to be empty.
  return [...buckets.keys()]
    .sort((a, b) => (GALLERY_SECTION_RANK[a] ?? Number.MAX_SAFE_INTEGER) - (GALLERY_SECTION_RANK[b] ?? Number.MAX_SAFE_INTEGER))
    .map((key) => ({ key, entries: buckets.get(key) as ComposerGalleryEntry[] }));
}
